import React, { useState, useEffect, useCallback } from 'react'
import { Container, Row, Col, Button } from 'react-bootstrap'
import { ChevronLeft, ChevronRight, Check } from 'lucide-react'
import styled from 'styled-components'
import { useProject } from './NewProjectContext'

const WizardContainer = styled(Container)`
  padding: 2rem;
  background: #2a2a2a;
`

const StepIndicator = styled.div<{ $isActive: boolean; $isCompleted: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${(props) =>
    props.$isCompleted
      ? 'linear-gradient(45deg, #402563, #114ee0)'
      : props.$isActive
      ? '#2a2a2a'
      : '#1a1a1a'};
  border: 2px solid
    ${(props) => (props.$isCompleted ? 'transparent' : props.$isActive ? '#114ee0' : '#404040')};
  color: ${(props) => (props.$isCompleted || props.$isActive ? '#fff' : '#666')};
  transition: all 0.3s ease;
  box-shadow: ${(props) => (props.$isCompleted ? '0 0 15px rgba(17, 78, 224, 0.3)' : 'none')};
`

const StepConnector = styled.div<{ $isCompleted: boolean }>`
  flex: 1;
  height: 2px;
  background: ${(props) =>
    props.$isCompleted ? 'linear-gradient(to right, #402563, #114ee0)' : '#404040'};
  margin: 0 0.5rem;
  transition: all 0.3s ease;
`

const StepLabel = styled.span<{ $isActive: boolean; $isCompleted: boolean }>`
  color: ${(props) =>
    props.$isCompleted || props.$isActive ? '#fff' : 'rgba(255, 255, 255, 0.5)'};
  font-weight: ${(props) => (props.$isActive ? '600' : '400')};
  font-size: 0.9rem;
  transition: all 0.3s ease;
`

const StepTitle = styled.h2`
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  text-align: center;
  margin: 2rem 0;
  background: linear-gradient(45deg, #402563, #114ee0);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  text-shadow: 0 0 20px rgba(17, 78, 224, 0.3);
`

const ValidationMessage = styled.div`
  color: #dc3545;
  font-size: 0.875rem;
  text-align: center;
  margin-top: 1rem;
  padding: 0.5rem;
  background: rgba(220, 53, 69, 0.1);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`

export type WizardStep = {
  name: string
  component: React.ComponentType<any>
  title: string
  validate?: (context: {
    projectName?: string
    projectType?: string
    description?: string
  }) => boolean
}

interface WizardProps {
  steps: WizardStep[]
  stepProps?: Record<string, any>
}

const Wizard: React.FC<WizardProps> = ({ steps, stepProps }): React.ReactElement => {
  const [currentStep, setCurrentStep] = useState(0)
  const [completedSteps, setCompletedSteps] = useState<number[]>([])
  const [showValidationMessage, setShowValidationMessage] = useState(false)
  const projectContext = useProject()

  const validateStep = useCallback(
    (stepIndex: number) => {
      const stepObj = steps[stepIndex]
      if (stepObj.validate) {
        return stepObj.validate(projectContext)
      }
      return true
    },
    [steps, projectContext]
  )

  const validateCurrentStep = useCallback(async () => {
    const isValid = validateStep(currentStep)
    if (!isValid) {
      setShowValidationMessage(true)
      setCompletedSteps((prev) => prev.filter((step) => step < currentStep))
      return false
    }
    setShowValidationMessage(false)
    return true
  }, [currentStep, validateStep, setShowValidationMessage, setCompletedSteps])

  const navigate = useCallback(
    async (direction: 'next' | 'previous') => {
      if (direction === 'next') {
        const isValid = await validateCurrentStep()
        if (isValid && currentStep < steps.length - 1) {
          setCompletedSteps((prev) => [...prev, currentStep])
          setCurrentStep(currentStep + 1)
        }
      } else {
        if (currentStep > 0) {
          const isCurrentValid = validateStep(currentStep)
          if (!isCurrentValid) {
            setCompletedSteps((prev) => prev.filter((step) => step < currentStep))
          }
          setCurrentStep(currentStep - 1)
          setShowValidationMessage(false)
        }
      }
    },
    [
      currentStep,
      steps.length,
      validateCurrentStep,
      validateStep,
      setCompletedSteps,
      setCurrentStep,
      setShowValidationMessage,
    ]
  )

  useEffect(() => {
    const handleKeyPress = async (event: KeyboardEvent) => {
      // Don't handle keyboard navigation if user is typing in an input or textarea
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return
      }

      switch (event.key.toLowerCase()) {
        case 'enter':
          if (!event.shiftKey) {
            event.preventDefault()
            await navigate('next')
          }
          break
        case 'escape':
          if (stepProps?.onHide) {
            stepProps.onHide()
          }
          break
        case 'arrowright':
          if (currentStep < steps.length - 1) {
            event.preventDefault()
            await navigate('next')
          }
          break
        case 'arrowleft':
          if (currentStep > 0) {
            event.preventDefault()
            await navigate('previous')
          }
          break
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => {
      window.removeEventListener('keydown', handleKeyPress)
    }
  }, [currentStep, steps.length, stepProps, navigate])

  const handleStepClick = async (index: number) => {
    if (index > currentStep) {
      const isValid = await validateCurrentStep()
      if (!isValid) {
        setCompletedSteps((prev) => prev.filter((step) => step < currentStep))
        return
      }
      setCompletedSteps((prev) => [...prev, currentStep])
    } else {
      const isCurrentValid = validateStep(currentStep)
      if (!isCurrentValid) {
        setCompletedSteps((prev) => prev.filter((step) => step < currentStep))
      }
    }

    if (index <= currentStep || completedSteps.includes(currentStep)) {
      setCurrentStep(index)
      setShowValidationMessage(false)
    }
  }

  // Update component references to use navigate
  const goToNext = useCallback(() => navigate('next'), [navigate])
  const goToPrevious = useCallback(() => navigate('previous'), [navigate])

  const CurrentStepComponent = steps[currentStep].component

  return (
    <WizardContainer>
      <Row className="mb-4">
        <Col>
          <div className="d-flex align-items-center justify-content-between">
            {steps.map((step, index) => (
              <React.Fragment key={index}>
                <div
                  className="d-flex align-items-center cursor-pointer"
                  onClick={() => handleStepClick(index)}
                  style={{
                    cursor:
                      index <= currentStep || completedSteps.includes(index - 1)
                        ? 'pointer'
                        : 'not-allowed',
                  }}
                >
                  <StepIndicator
                    $isActive={index === currentStep}
                    $isCompleted={completedSteps.includes(index)}
                  >
                    {completedSteps.includes(index) ? <Check size={18} /> : index + 1}
                  </StepIndicator>
                  <StepLabel
                    className="ms-2"
                    $isActive={index === currentStep}
                    $isCompleted={completedSteps.includes(index)}
                  >
                    {step.name}
                  </StepLabel>
                </div>
                {index < steps.length - 1 && (
                  <StepConnector $isCompleted={completedSteps.includes(index)} />
                )}
              </React.Fragment>
            ))}
          </div>
        </Col>
      </Row>

      <StepTitle>{steps[currentStep].title}</StepTitle>

      <Row className="g-0" style={{ minHeight: '384px' }}>
        {currentStep > 0 && (
          <Col xs="auto" className="d-flex align-items-center pe-4">
            <Button
              variant="link"
              onClick={goToPrevious}
              className="d-flex align-items-center justify-content-center"
              style={{ width: '40px', height: '40px', padding: 0 }}
            >
              <ChevronLeft size={40} />
            </Button>
          </Col>
        )}

        <Col
          style={{
            alignContent: 'center',
            justifyContent: 'center',
            minHeight: '384px',
          }}
        >
          <CurrentStepComponent {...stepProps} onNext={goToNext} />
          {showValidationMessage && (
            <ValidationMessage>
              <span>Please complete this step before proceeding</span>
            </ValidationMessage>
          )}
        </Col>

        {currentStep < steps.length - 1 && (
          <Col xs="auto" className="d-flex align-items-center ps-4">
            <Button
              variant="link"
              onClick={goToNext}
              className="d-flex align-items-center justify-content-center"
              style={{ width: '40px', height: '40px', padding: 0 }}
            >
              <ChevronRight size={40} />
            </Button>
          </Col>
        )}
      </Row>
    </WizardContainer>
  )
}

export default Wizard
